/**
 * RouterConfig — generates React Router <Route> elements from the route manifest.
 *
 * Replaces the 146 inline <Route> declarations that were in App.tsx.
 * Component lazy-imports and RBAC wrapping are driven entirely by MANIFEST entries.
 */

import React, { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import { Route, Navigate } from 'react-router-dom';
import { MANIFEST } from './manifest';
import ProtectedRoute from '../components/layout/ProtectedRoute';
import AppLayout from '../components/layout/AppLayout';

// ─── Lazy component registry ───────────────────────────────────────────────
// Maps manifest `component` string → React.lazy import.
// Import paths match the original App.tsx exactly.

const COMPONENTS: Record<string, LazyExoticComponent<ComponentType<Record<string, never>>>> = {
  // Public
  LandingPage: lazy(() => import('../pages/LandingPage')),
  LoginPage: lazy(() => import('../pages/LoginPage')),

  // Dashboard
  DashboardPage: lazy(() => import('../pages/dashboard/DashboardPage')),

  // Data Entry
  PremiumsPage: lazy(() => import('../pages/data-entry/PremiumsPage')),
  ClaimsPage: lazy(() => import('../pages/data-entry/ClaimsPage')),
  YieldCurvePage: lazy(() => import('../pages/data-entry/YieldCurvePage')),
  UPRPage: lazy(() => import('../pages/data-entry/UPRPage')),
  DACPage: lazy(() => import('../pages/data-entry/DACPage')),
  RevenueAccountPage: lazy(() => import('../pages/data-entry/RevenueAccountPage')),
  RiskAdjustmentPage: lazy(() => import('../pages/data-entry/RiskAdjustmentPage')),
  ExpensesPage: lazy(() => import('../pages/data-entry/ExpensesPage')),
  TrianglesPage: lazy(() => import('../pages/data-entry/TrianglesPage')),
  RetentionRatesPage: lazy(() => import('../pages/data-entry/RetentionRatesPage')),
  CommissionsPage: lazy(() => import('../pages/data-entry/CommissionsPage')),
  ExpenseAllocationsPage: lazy(() => import('../pages/data-entry/ExpenseAllocationsPage')),
  IFRS4BalanceSheetPage: lazy(() => import('../pages/data-entry/IFRS4BalanceSheetPage')),
  BalanceSheetEntryPage: lazy(() => import('../pages/data-entry/BalanceSheetEntryPage')),
  BulkUploadPage: lazy(() => import('../pages/data-entry/BulkUploadPage')),
  GMMDataEntryPage: lazy(() => import('../pages/data-entry/GMMDataEntryPage')),
  VFADataEntryPage: lazy(() => import('../pages/data-entry/VFADataEntryPage')),
  ReinsurancePage: lazy(() => import('../pages/data-entry/ReinsurancePage')),
  LapseTablesPage: lazy(() => import('../pages/data-entry/LapseTablesPage')),
  LifeAssumptionsPage: lazy(() => import('../pages/data-entry/LifeAssumptionsPage')),
  LTRevenueAccountPage: lazy(() => import('../pages/data-entry/LTRevenueAccountPage')),

  // Calculations
  CalculationsPage: lazy(() => import('../pages/calculations/CalculationsPage')),
  LifeCalculationPage: lazy(() => import('../pages/calculations/LifeCalculationPage')),
  JobStatusPage: lazy(() => import('../pages/calculations/JobStatusPage')),

  // Disclosures
  DisclosuresPage: lazy(() => import('../pages/disclosures/DisclosuresPage')),
  SensitivityPage: lazy(() => import('../pages/disclosures/SensitivityPage')),
  GMMReconciliationPage: lazy(() => import('../pages/disclosures/GMMReconciliationPage')),
  RiskDisclosuresPage: lazy(() => import('../pages/disclosures/RiskDisclosuresPage')),
  ClaimsDevelopmentPage: lazy(() => import('../pages/disclosures/ClaimsDevelopmentPage')),
  NarrativeDisclosuresPage: lazy(() => import('../pages/disclosures/NarrativeDisclosuresPage')),
  PeriodManagementPage: lazy(() => import('../pages/disclosures/PeriodManagementPage')),
  LifeDisclosuresPage: lazy(() => import('../pages/disclosures/LifeDisclosuresPage')),
  ModelValidationPage: lazy(() => import('../pages/disclosures/ModelValidationPage')),

  // Financial Statements
  FinancialStatementsPage: lazy(() => import('../pages/financial-statements/FinancialStatementsPage')),

  // XBRL
  XBRLPage: lazy(() => import('../pages/xbrl/XBRLPage')),

  // Analysis
  IFRS4ConversionPage: lazy(() => import('../pages/analysis/IFRS4ConversionPage')),
  ReserveResultsPage: lazy(() => import('../pages/analysis/ReserveResultsPage')),

  // Company
  CompanyInfoPage: lazy(() => import('../pages/company/CompanyInfoPage')),
  BusinessClassesPage: lazy(() => import('../pages/company/BusinessClassesPage')),
  IFRS17PoliciesPage: lazy(() => import('../pages/company/IFRS17PoliciesPage')),
  ContractGroupsPage: lazy(() => import('../pages/company/ContractGroupsPage')),

  // Approvals
  ApprovalsPage: lazy(() => import('../pages/approvals/ApprovalsPage')),
  CompletenessPage: lazy(() => import('../pages/common/ComingSoonPage')),

  // Import/Export
  ImportExportPage: lazy(() => import('../pages/import-export/ImportExportPage')),

  // Budgeting
  BudgetingDashboard: lazy(() => import('../pages/budgeting/BudgetingDashboard')),
  BudgetDashboardPage: lazy(() => import('../pages/budgeting/BudgetDashboardPage')),
  BudgetAssumptionsPage: lazy(() => import('../pages/budgeting/BudgetAssumptionsPage')),
  BudgetVariancePage: lazy(() => import('../pages/budgeting/BudgetVariancePage')),

  // Actuarial Model
  ActuarialModelDashboard: lazy(() => import('../pages/actuarial/ActuarialModelDashboard')),
  MethodSelectionPage: lazy(() => import('../pages/actuarial-model/MethodSelectionPage')),
  StochasticReservingPage: lazy(() => import('../pages/actuarial-model/StochasticReservingPage')),
  ExperienceAnalysisPage: lazy(() => import('../pages/actuarial-model/ExperienceAnalysisPage')),
  AssumptionGovernancePage: lazy(() => import('../pages/actuarial-model/AssumptionGovernancePage')),
  EngineManualPage: lazy(() => import('../pages/actuarial-model/EngineManualPage')),
  GMMPolicyInputPage: lazy(() => import('../pages/actuarial-model/GMMPolicyInputPage')),
  GMMTestRunnerPage: lazy(() => import('../pages/actuarial-model/GMMTestRunnerPage')),
  GMMComparisonPage: lazy(() => import('../pages/actuarial-model/GMMComparisonPage')),
  CSMRollForwardPage: lazy(() => import('../pages/actuarial-model/CSMRollForwardPage')),
  RiskAggregationPage: lazy(() => import('../pages/actuarial-model/RiskAggregationPage')),
  ActActiveProductsPage: lazy(() => import('../pages/actuarial-model/ActActiveProductsPage')),
  ProductLinkagePage: lazy(() => import('../pages/actuarial-model/ProductLinkagePage')),

  // Shared
  IFRS17TraceabilityPage: lazy(() => import('../pages/shared/IFRS17TraceabilityPage')),

  // Reinsurance Module
  ReinsuranceDashboard: lazy(() => import('../pages/reinsurance/ReinsuranceDashboard')),
  TreatyManagementPage: lazy(() => import('../pages/reinsurance/TreatyManagementPage')),
  PerformanceDashboardPage: lazy(() => import('../pages/reinsurance/PerformanceDashboardPage')),
  AdequacyReportPage: lazy(() => import('../pages/reinsurance/AdequacyReportPage')),

  // Pricing Module
  PricingDashboard: lazy(() => import('../pages/pricing/PricingDashboard')),
  ProductManagementPage: lazy(() => import('../pages/pricing/ProductManagementPage')),
  ActiveProductsPage: lazy(() => import('../pages/pricing/ActiveProductsPage')),
  ActiveProductCommissionsPage: lazy(() => import('../pages/pricing/ActiveProductCommissionsPage')),
  ActiveProductExpensesPage: lazy(() => import('../pages/pricing/ActiveProductExpensesPage')),
  ActiveProductLapseRatesPage: lazy(() => import('../pages/pricing/ActiveProductLapseRatesPage')),
  PricingAnalysisPage: lazy(() => import('../pages/pricing/PricingAnalysisPage')),
  ProfitTestPage: lazy(() => import('../pages/pricing/ProfitTestPage')),
  CertificatesPage: lazy(() => import('../pages/pricing/CertificatesPage')),

  // Life Profit Testing
  LifePricingDashboardPage: lazy(() => import('../pages/pricing/life/LifePricingDashboardPage')),
  LifeModelPointsPage: lazy(() => import('../pages/pricing/life/LifeModelPointsPage')),
  LifeProfitTestRunPage: lazy(() => import('../pages/pricing/life/LifeProfitTestRunPage')),
  LifeProfitTestHistoryPage: lazy(() => import('../pages/pricing/life/LifeProfitTestHistoryPage')),

  // Life Insurance
  LifeContractGroupsPage: lazy(() => import('../pages/life/LifeContractGroupsPage')),
  LifeReconciliationPage: lazy(() => import('../pages/life/LifeReconciliationPage')),
  LifeSensitivityPage: lazy(() => import('../pages/life/LifeSensitivityPage')),
  LifeAssetSharePage: lazy(() => import('../pages/life/LifeAssetSharePage')),

  // FCR Module
  FCRDashboard: lazy(() => import('../pages/fcr/FCRDashboard')),
  FCRDashboardPage: lazy(() => import('../pages/fcr/FCRDashboardPage')),
  FCRFinancialDataPage: lazy(() => import('../pages/fcr/FCRFinancialDataPage')),
  FCRBusinessMixPage: lazy(() => import('../pages/fcr/FCRBusinessMixPage')),
  FCRCapitalPage: lazy(() => import('../pages/fcr/FCRCapitalPage')),
  FCRAssetAllocationPage: lazy(() => import('../pages/fcr/FCRAssetAllocationPage')),
  FCRReinsuranceDataPage: lazy(() => import('../pages/fcr/FCRReinsuranceDataPage')),
  FCRGovernancePage: lazy(() => import('../pages/fcr/FCRGovernancePage')),
  FCRRecommendationsPage: lazy(() => import('../pages/fcr/FCRRecommendationsPage')),
  FCRRiskAppetitePage: lazy(() => import('../pages/fcr/FCRRiskAppetitePage')),
  FCRSectionNarrativesPage: lazy(() => import('../pages/fcr/FCRSectionNarrativesPage')),
  SolvencyAnalysisPage: lazy(() => import('../pages/fcr/SolvencyAnalysisPage')),
  RiskHeatmapPage: lazy(() => import('../pages/fcr/RiskHeatmapPage')),
  FCRStressTestPage: lazy(() => import('../pages/fcr/StressTestPage')),
  FCRReconciliationPage: lazy(() => import('../pages/fcr/FCRReconciliationPage')),
  FCRReportViewerPage: lazy(() => import('../pages/fcr/FCRReportViewerPage')),

  // Policy Data
  PolicyDataDashboardPage: lazy(() => import('../pages/policy-data/PolicyDataDashboardPage')),
  PolicyDataUploadPage: lazy(() => import('../pages/policy-data/PolicyDataUploadPage')),
  ClaimsDataEntryPage: lazy(() => import('../pages/policy-data/ClaimsDataEntryPage')),
  EndorsementsPage: lazy(() => import('../pages/policy-data/EndorsementsPage')),
  MemberRegistryPage: lazy(() => import('../pages/policy-data/MemberRegistryPage')),
  MembershipMovementsPage: lazy(() => import('../pages/policy-data/MembershipMovementsPage')),
  CohortCategoriesPage: lazy(() => import('../pages/policy-data/CohortCategoriesPage')),
  CohortSummaryPage: lazy(() => import('../pages/policy-data/CohortSummaryPage')),

  // Admin
  AdminDashboardPage: lazy(() => import('../pages/admin/AdminDashboardPage')),
  CountriesPage: lazy(() => import('../pages/admin/CountriesPage')),
  CountryDetailPage: lazy(() => import('../pages/admin/CountryDetailPage')),
  CountryBusinessClassesPage: lazy(() => import('../pages/admin/CountryBusinessClassesPage')),
  CompaniesPage: lazy(() => import('../pages/admin/CompaniesPage')),
  UsersPage: lazy(() => import('../pages/admin/UsersPage')),
  AdminYieldCurvesPage: lazy(() => import('../pages/admin/AdminYieldCurvesPage')),
  AdminMortalityTablesPage: lazy(() => import('../pages/admin/AdminMortalityTablesPage')),
  AdminReinsurersPage: lazy(() => import('../pages/admin/AdminReinsurersPage')),
  AdminCommissionSignaturesPage: lazy(() => import('../pages/admin/AdminCommissionSignaturesPage')),
  IntegrationSettingsPage: lazy(() => import('../pages/admin/IntegrationSettingsPage')),
  SystemMetricsPage: lazy(() => import('../pages/admin/SystemMetricsPage')),
  SystemLimitsPage: lazy(() => import('../pages/admin/SystemLimitsPage')),
  FeatureFlagsPage: lazy(() => import('../pages/admin/FeatureFlagsPage')),
  ScheduledReportsPage: lazy(() => import('../pages/admin/ScheduledReportsPage')),
  JobDashboardPage: lazy(() => import('../pages/admin/JobDashboardPage')),

  // Help
  GlossaryPage: lazy(() => import('../pages/help/GlossaryPage')),

  // Profile / Community
  MyProfilePage: lazy(() => import('../pages/profile/MyProfilePage')),
  UserProfilePage: lazy(() => import('../pages/profile/UserProfilePage')),
  DirectoryPage: lazy(() => import('../pages/profile/DirectoryPage')),
  MessagesPage: lazy(() => import('../pages/messages/MessagesPage')),
};

// ─── Public paths that render outside the AppLayout / ProtectedRoute shell ──
const PUBLIC_PATHS = new Set(['/', 'login']);

// ─── Route element builder ─────────────────────────────────────────────────

/**
 * Build the element for a single manifest entry.
 *
 * - readOnly entries pass `readOnly` as a prop (for "provided/*" data review routes).
 * - Entries with non-empty allowedRoles get an inner ProtectedRoute wrapper.
 * - Entries with empty allowedRoles inside the layout shell render without
 *   an extra ProtectedRoute (the outer shell already enforces authentication).
 */
function buildElement(entry: (typeof MANIFEST)[number]) {
  const Component = COMPONENTS[entry.component];
  if (!Component) {
    console.warn(`[RouterConfig] Unknown component "${entry.component}" for path "${entry.path}"`);
    return null;
  }

  const props: Record<string, unknown> = {};
  if (entry.readOnly) {
    props.readOnly = true;
  }

  const element = <Component {...props} />;

  // Routes with specified allowedRoles get an inner ProtectedRoute
  if (entry.allowedRoles.length > 0) {
    return <ProtectedRoute allowedRoles={entry.allowedRoles}>{element}</ProtectedRoute>;
  }

  return element;
}

// ─── Route builder ──────────────────────────────────────────────────────────

/**
 * Build all route elements from the manifest.
 *
 * Returns a flat array of <Route> and layout-<Route> elements suitable
 * for spreading directly inside a <Routes> parent in App.tsx.
 */
export function buildRoutes(): React.ReactNode[] {
  const publicEntries = MANIFEST.filter((e) => PUBLIC_PATHS.has(e.path));
  // Deduplicate by path — first manifest entry wins for any given path
  const seen = new Set<string>();
  const protectedEntries = MANIFEST.filter((e) => {
    if (PUBLIC_PATHS.has(e.path)) return false;
    if (seen.has(e.path)) return false;
    seen.add(e.path);
    return true;
  });

  return [
    /* Public routes — no layout, no auth */
    ...publicEntries.map((entry) => {
      const Component = COMPONENTS[entry.component];
      if (!Component) return null;
      // entry.path is '/' for landing or 'login' — use as-is to avoid '//'
      const routePath = entry.path.startsWith('/') ? entry.path : `/${entry.path}`;
      return <Route key={entry.path} path={routePath} element={<Component />} />;
    }),

    /* Protected routes — wrapped in auth check + AppLayout */
    <Route
      key="__protected__"
      element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }
    >
      {protectedEntries.map((entry) => (
        <Route key={entry.path} path={entry.path} element={buildElement(entry)} />
      ))}
    </Route>,

    /* 404 catch-all: redirect unknown paths to dashboard */
    <Route key="__catchall__" path="*" element={<Navigate to="/dashboard" replace />} />,
  ];
}
